import React, { useEffect, useRef } from 'react';

/**
 * Self-contained, dependency-free animated "loading" placeholder for the ~10-12s it takes
 * services/data/fusebox-embed-image to embed a photo.
 *
 * Given an image url it draws the image into a canvas, runs a one-time Sobel edge pass, and then
 * animates a linear scan that progressively reveals the detected edges (glowing cyan) under a
 * bright moving scan line — as if a machine were tracing the borders of the photo. The scan is a
 * 5s horizontal sweep (left → right) followed by a 5s vertical sweep (top → bottom), filling the
 * full 10s embedding wait; it loops if embedding runs longer.
 *
 * Everything is computed once and the per-frame work is a handful of drawImage/gradient calls,
 * driven by requestAnimationFrame and paused via the document visibility API. No external deps.
 *
 * @param {Object} props
 * @param {string} props.imageUrl        image to draw / scan
 * @param {number} [props.durationMs]    full scan cycle (default 10000 = 5s + 5s)
 * @param {string}   [props.label]      default caption, used for any pass without its own message
 * @param {string[]} [props.messages]   per-pass captions (index 0..3); falls back to `label`
 */
export default function ImageProcessingEdgeFxLoader({ imageUrl, durationMs = 10000, label = 'Embedding image…', messages = null }) {
  const canvasRef = useRef(null);
  const captionRef = useRef(null);

  // Resolve a caption for each of the 4 passes, kept in a ref so message changes don't restart the
  // animation (which would reload the image).
  const messagesRef = useRef(null);
  messagesRef.current = [0, 1, 2, 3].map(i => (messages && messages[i] != null ? messages[i] : label));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const MAX_DIM = 480; // cap internal resolution so the Sobel pass and per-frame compositing stay cheap
    const ctx = canvas.getContext('2d');

    let raf = 0;
    let start = 0;
    let cancelled = false;
    let lastIdx = -1; // track pass changes so the caption updates only on transition

    // Offscreen buffers, allocated once the image dimensions are known.
    let W = 0, H = 0;
    let srcCanvas = null;   // resized source image
    let edgeCanvas = null;  // glowing edges on transparent bg (computed once)
    let maskCanvas = null;  // per-frame reveal gradient
    let glowCanvas = null;  // per-frame: edges ∩ mask
    let mctx = null, gctx = null;
    let edgeOk = false;     // false when the image is cross-origin tainted (degrade gracefully)


    function makeCanvas(w, h) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      return c;
    }

    /** One-time Sobel edge detection from srcCanvas -> edgeCanvas (glowing cyan, alpha = edge strength). */
    function computeEdges() {
      let src;
      try {
        src = srcCanvas.getContext('2d').getImageData(0, 0, W, H);
      } catch (e) {
        return false; // tainted canvas (no CORS) — skip edges, we'll still animate the scan line
      }
      const data = src.data;
      const n = W * H;

      // Grayscale (luma) into a Float32Array.
      const gray = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        const o = i << 2;
        gray[i] = 0.299 * data[o] + 0.587 * data[o + 1] + 0.114 * data[o + 2];
      }

      const out = ctx.createImageData(W, H);
      const od = out.data;
      // Sobel kernels, skipping the 1px border.
      for (let y = 1; y < H - 1; y++) {
        for (let x = 1; x < W - 1; x++) {
          const i = y * W + x;
          const tl = gray[i - W - 1], tc = gray[i - W], tr = gray[i - W + 1];
          const ml = gray[i - 1],                       mr = gray[i + 1];
          const bl = gray[i + W - 1], bc = gray[i + W], br = gray[i + W + 1];

          const gx = (tr + 2 * mr + br) - (tl + 2 * ml + bl);
          const gy = (bl + 2 * bc + br) - (tl + 2 * tc + tr);
          let mag = Math.sqrt(gx * gx + gy * gy);

          // Scale + soft threshold so faint texture stays dim and real borders pop.
          mag = mag > 28 ? Math.min(255, mag * 1.4) : mag * 0.25;

          const o = i << 2;
          od[o] = 255; od[o + 1] = 255; od[o + 2] = 255; // white; tinted per-pass at draw time
          od[o + 3] = mag; // alpha carries edge strength
        }
      }
      edgeCanvas.getContext('2d').putImageData(out, 0, 0);
      return true;
    }

    /** Bright scan line + glow along a vertical line at canvas-x `px`, tinted `r,g,b`. */
    function scanLineVertical(px, tint) {
      const gw = Math.max(8, W * 0.05);
      const g = ctx.createLinearGradient(px - gw, 0, px + gw, 0);
      g.addColorStop(0, `rgba(${tint},0)`);
      g.addColorStop(0.5, `rgba(${tint},0.55)`);
      g.addColorStop(1, `rgba(${tint},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(px - gw, 0, gw * 2, H);
      ctx.fillStyle = 'rgba(245,245,255,0.9)'; // crisp white core
      ctx.fillRect(px - 0.75, 0, 1.5, H);
    }

    /** Bright scan line + glow along a horizontal line at canvas-y `py`, tinted `r,g,b`. */
    function scanLineHorizontal(py, tint) {
      const gw = Math.max(8, H * 0.05);
      const g = ctx.createLinearGradient(0, py - gw, 0, py + gw);
      g.addColorStop(0, `rgba(${tint},0)`);
      g.addColorStop(0.5, `rgba(${tint},0.55)`);
      g.addColorStop(1, `rgba(${tint},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(0, py - gw, W, gw * 2);
      ctx.fillStyle = 'rgba(245,245,255,0.9)';
      ctx.fillRect(0, py - 0.75, W, 1.5);
    }

    const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);

    /**
     * Build a moving reveal band into maskCanvas: transparent ahead of the line, a bright peak at
     * the line, then a fading trail behind it. As the band travels, edges it revealed fade back out
     * once the line has moved on. `alongY` runs the band vertically (top → bottom) instead of
     * horizontally (left → right).
     */
    function buildBandMask(p, alongY, rev) {
      const trail = 0.28;   // how far the glow lingers behind the line
      const feather = 0.05; // soft leading edge ahead of the line
      // The trail lags on the side the line is coming from, which flips when the pass is reversed.
      const lo = clamp01(p - (rev ? feather : trail));
      const hi = clamp01(p + (rev ? trail : feather));
      const at = clamp01(p);

      mctx.clearRect(0, 0, W, H);
      const mg = alongY
        ? mctx.createLinearGradient(0, 0, 0, H)
        : mctx.createLinearGradient(0, 0, W, 0);
      mg.addColorStop(0, 'rgba(255,255,255,0)');
      mg.addColorStop(lo, 'rgba(255,255,255,0)');
      mg.addColorStop(at, 'rgba(255,255,255,1)');
      mg.addColorStop(hi, 'rgba(255,255,255,0)');
      mg.addColorStop(1, 'rgba(255,255,255,0)');
      mctx.fillStyle = mg;
      mctx.fillRect(0, 0, W, H);
    }

    // Four passes filling the cycle, each twice as fast as a half-cycle pass, each its own tint:
    // vertical ↓ cyan, horizontal → magenta, vertical ↑ yellow, horizontal ← ultraviolet.
    const PASSES = [
      { alongY: true, rev: false, tint: '130,220,255' },  // cyan
      { alongY: false, rev: false, tint: '255,110,210' }, // magenta
      { alongY: true, rev: true, tint: '255,220,110' },   // yellow
      { alongY: false, rev: true, tint: '170,90,255' },   // ultraviolet
    ];
    const quarter = durationMs / 4;

    function frame(now) {
      if (cancelled) return;
      if (!start) start = now;
      const elapsed = (now - start) % durationMs;
      const idx = Math.min(3, Math.floor(elapsed / quarter)); // which of the 4 passes
      const { alongY, rev, tint } = PASSES[idx];
      const local = (elapsed - idx * quarter) / quarter;      // 0..1 within the pass
      const p = rev ? 1 - local : local;                      // reversed passes run end → start

      if (idx !== lastIdx) {
        lastIdx = idx;
        if (captionRef.current) captionRef.current.textContent = messagesRef.current[idx];
      }

      ctx.clearRect(0, 0, W, H);

      // Dimmed base photo, always visible.
      ctx.globalAlpha = 0.5;
      ctx.drawImage(srcCanvas, 0, 0);
      ctx.globalAlpha = 1;

      if (edgeOk) {
        // Reveal only the edges inside the travelling band (edges ∩ mask), so they glow near the
        // scan line and fade out behind it.
        buildBandMask(p, alongY, rev);
        gctx.clearRect(0, 0, W, H);
        gctx.drawImage(edgeCanvas, 0, 0);
        gctx.globalCompositeOperation = 'destination-in';
        gctx.drawImage(maskCanvas, 0, 0);
        gctx.globalCompositeOperation = 'source-in'; // recolor the masked (white) edges to this pass's tint
        gctx.fillStyle = `rgb(${tint})`;
        gctx.fillRect(0, 0, W, H);
        gctx.globalCompositeOperation = 'source-over';

        ctx.globalCompositeOperation = 'lighter';
        ctx.drawImage(glowCanvas, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
      }

      ctx.globalCompositeOperation = 'lighter';
      if (alongY) scanLineHorizontal(p * H, tint);
      else scanLineVertical(p * W, tint);
      ctx.globalCompositeOperation = 'source-over';

      raf = requestAnimationFrame(frame);
    }

    function startLoop() {
      cancelAnimationFrame(raf);
      start = 0;
      raf = requestAnimationFrame(frame);
    }

    function onVisibility() {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else if (srcCanvas && !cancelled) {
        startLoop();
      }
    }

    function onLoaded(img) {
      if (cancelled) return;
      const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
      W = Math.max(1, Math.round(img.naturalWidth * scale));
      H = Math.max(1, Math.round(img.naturalHeight * scale));

      canvas.width = W;
      canvas.height = H;
      canvas.style.opacity = '1';

      srcCanvas = makeCanvas(W, H);
      srcCanvas.getContext('2d').drawImage(img, 0, 0, W, H);

      edgeCanvas = makeCanvas(W, H);
      maskCanvas = makeCanvas(W, H);
      glowCanvas = makeCanvas(W, H);
      mctx = maskCanvas.getContext('2d');
      gctx = glowCanvas.getContext('2d');

      edgeOk = computeEdges();

      document.addEventListener('visibilitychange', onVisibility);
      startLoop();
    }

    // Try a CORS load first (needed to read pixels for edges). If that fails, retry without
    // crossOrigin so the photo still displays (edges disabled). If even that fails, give up quietly.
    function load(useCors) {
      const img = new Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => onLoaded(img);
      img.onerror = () => { if (!cancelled) { if (useCors) load(false); } };
      img.src = imageUrl;
    }
    load(true);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [imageUrl, durationMs]);

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: 0,
        flex: '1 1 auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          minHeight: 0,
          objectFit: 'contain',
          borderRadius: 4,
          background: '#0b1622',
          opacity: 0,
          transition: 'opacity 0.3s ease',
        }}
      />
      {/* Centered over the whole area: before the image loads it sits in the middle of the space,
          and since the image is centered too, it lands on the image's center once loaded. */}
      <div
        ref={captionRef}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: '90%',
          padding: '6px 14px',
          background: 'rgba(0,0,0,0.65)',
          color: '#fff',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          fontSize: 14,
          lineHeight: 1.3,
          textAlign: 'center',
          borderRadius: 14,
          pointerEvents: 'none',
        }}
      >
        {messagesRef.current[0]}
      </div>
    </div>
  );
}
